"use client";

import { Button } from "@cmp-src/primitives/button";
import { ChevronDown, ChevronUp, X } from "lucide-react";

interface DetailSidebarNavBarProps {
  /** Zero-based position of the open item; omit to hide the prev/next controls. */
  navIndex?: number;
  /** Total items to page through. */
  navTotal?: number;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  onClose?: () => void;
  /** Noun used in the prev/next aria labels ("Previous request"). */
  itemNoun?: string;
}

/**
 * Top bar of a detail modal's sidebar column: prev/next paging with an
 * "x of y" counter, plus the close button. Shared by the workplace-request and
 * reservation detail modals so both navigate identically.
 */
export const DetailSidebarNavBar = ({
  navIndex,
  navTotal,
  onNavigatePrev,
  onNavigateNext,
  onClose,
  itemNoun = "item"
}: DetailSidebarNavBarProps) => {
  const hasNav =
    typeof navIndex === "number" &&
    navIndex >= 0 &&
    typeof navTotal === "number" &&
    navTotal > 0;

  return (
    <div className="flex shrink-0 items-center justify-end gap-2 px-4 py-3">
      {hasNav && (
        <div className="mr-auto flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={onNavigatePrev}
            disabled={!onNavigatePrev}
            aria-label={`Previous ${itemNoun}`}
            className="h-7 w-7 text-neutral-500 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={onNavigateNext}
            disabled={!onNavigateNext}
            aria-label={`Next ${itemNoun}`}
            className="h-7 w-7 text-neutral-500 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <span className="ml-1 font-inter text-neutral-500 text-xs">
            {(navIndex ?? 0) + 1} of {navTotal}
          </span>
        </div>
      )}
      <Button
        variant="outline"
        size="icon"
        onClick={onClose}
        aria-label="Close"
        className="h-7 w-7 text-neutral-500"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
